import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { FileDown, Languages, Upload } from "lucide-react";
import { PictogramSectionContent, VehiclePictogram } from "@/components/PictogramList";
import { toast } from "sonner";

const VALIDATOR_ROLES = ["ADMIN", "VALIDATEUR"];
const STAFF_ROLES = ["ADMIN", "VALIDATEUR", "REDACTEUR"];
const CADRE_FIELDS = [
  "entreprise_accueil_nom", "entreprise_accueil_coordonnees",
  "entreprise_transport_nom", "entreprise_transport_coordonnees",
  "responsable_site_nom", "responsable_site_fonction",
  "lieu_operation", "operation_repetitive",
  "tonnage_volume", "conditionnement",
  "adr_classe", "adr_code_danger", "adr_numero_onu", "adr_groupe_emballage",
  "materiel_disposition", "urgence_numero_site",
];
const CADRE_SHEET_KEYS = {
  entreprise_accueil_nom: "Entreprise_Accueil_Nom", entreprise_accueil_coordonnees: "Entreprise_Accueil_Coordonnees",
  entreprise_transport_nom: "Entreprise_Transport_Nom", entreprise_transport_coordonnees: "Entreprise_Transport_Coordonnees",
  responsable_site_nom: "Responsable_Site_Nom", responsable_site_fonction: "Responsable_Site_Fonction",
  lieu_operation: "Lieu_Operation", operation_repetitive: "Operation_Repetitive",
  tonnage_volume: "Tonnage_Volume", conditionnement: "Conditionnement",
  adr_classe: "ADR_Classe", adr_code_danger: "ADR_Code_Danger", adr_numero_onu: "ADR_Numero_ONU", adr_groupe_emballage: "ADR_Groupe_Emballage",
  materiel_disposition: "Materiel_Disposition", urgence_numero_site: "Urgence_Numero_Site",
};

export default function ProtocolDetail() {
  const { protocolId } = useParams();
  const { user } = useAuth();
  const { t } = useI18n();
  const [protocol, setProtocol] = useState(null);
  const [versions, setVersions] = useState([]);
  const [historique, setHistorique] = useState([]);
  const [referentiel, setReferentiel] = useState(null);
  const [traductions, setTraductions] = useState([]);
  const [epiList, setEpiList] = useState([]);
  const [risquesList, setRisquesList] = useState([]);
  const [vehiculesList, setVehiculesList] = useState([]);
  const [translating, setTranslating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [motif, setMotif] = useState("");
  const [versionType, setVersionType] = useState("MINEURE");
  const [uploadingPlan, setUploadingPlan] = useState(false);
  const [langues, setLangues] = useState([]);
  const [targetLang, setTargetLang] = useState("");

  useEffect(() => {
    api.get("/referentiel/epi").then((res) => setEpiList(res.data));
    api.get("/referentiel/risques").then((res) => setRisquesList(res.data));
    api.get("/vehicules").then((res) => setVehiculesList(res.data));
    api.get("/langues").then((res) => {
      setLangues(res.data);
      if (res.data.length > 0) setTargetLang(res.data[0].code);
    });
  }, []);

  const load = useCallback(() => {
    api.get(`/protocoles/${protocolId}`).then((res) => setProtocol(res.data));
    api.get(`/protocoles/${protocolId}/versions`).then((res) => setVersions(res.data));
    api.get(`/protocoles/${protocolId}/historique`).then((res) => setHistorique(res.data));
    api.get(`/protocoles/${protocolId}/referentiel-applicable`).then((res) => setReferentiel(res.data));
    api.get(`/protocoles/${protocolId}/traductions`).then((res) => setTraductions(res.data));
  }, [protocolId]);

  useEffect(() => {
    load();
  }, [load]);

  const canStaff = STAFF_ROLES.includes(user?.Role);
  const canValidate = VALIDATOR_ROLES.includes(user?.Role);

  const action = async (path, body, successMsg) => {
    try {
      await api.post(`/protocoles/${protocolId}/${path}`, body || {});
      toast.success(successMsg);
      setMotif("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = () => {
    const cadre = {};
    CADRE_FIELDS.forEach((f) => {
      const sheetKey = CADRE_SHEET_KEYS[f];
      cadre[f] = f === "operation_repetitive" ? !!protocol[sheetKey] : protocol[sheetKey] || "";
    });
    setEditForm({
      titre: protocol.Titre,
      description: protocol.Description,
      date_echeance: protocol.Date_Echeance || "",
      sections: protocol.sections.map((s) => ({ titre_section: s.Titre_Section, type_section: s.Type_Section, contenu: s.Contenu })),
      ...cadre,
    });
    setEditMode(true);
  };

  const updateEditSection = (idx, field, value) => {
    setEditForm({ ...editForm, sections: editForm.sections.map((s, i) => (i === idx ? { ...s, [field]: value } : s)) });
  };

  const saveEdit = async () => {
    try {
      await api.put(`/protocoles/${protocolId}`, editForm);
      toast.success(t("common.save"));
      setEditMode(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.post(`/protocoles/${protocolId}/pdf`, {}, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${protocol.Protocol_ID}_v${protocol.Version_Courante}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur PDF");
    }
  };

  const pollTranslation = async (traductionId, attempts = 0) => {
    if (attempts > 40) {
      toast.error(t("common.translation_timeout"));
      setTranslating(false);
      return;
    }
    try {
      const res = await api.get(`/protocoles/${protocolId}/traductions`);
      const row = res.data.find((r) => r.Traduction_ID === traductionId);
      if (!row || row.Statut === "EN_COURS") {
        setTimeout(() => pollTranslation(traductionId, attempts + 1), 3000);
        return;
      }
      setTranslating(false);
      if (row.Statut === "ECHEC") {
        toast.error(t("common.translation_failed"));
      } else {
        toast.success(t("common.translation"));
      }
      load();
    } catch (err) {
      setTranslating(false);
      toast.error(t("common.translation_failed"));
    }
  };

  const translate = async (langue) => {
    const langueCible = langue || targetLang;
    if (!langueCible) return;
    setTranslating(true);
    try {
      const res = await api.post(`/protocoles/${protocolId}/traduire`, { langue: langueCible });
      toast.info(t("common.translating"));
      pollTranslation(res.data.traduction_id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de traduction");
      setTranslating(false);
    }
  };

  const uploadPlanCirculation = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPlan(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      await api.post(`/protocoles/${protocolId}/plan-circulation`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("cadre.plan_circulation_uploaded"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setUploadingPlan(false);
      e.target.value = "";
    }
  };

  if (!protocol) {
    return (
      <div className="p-6 text-slate-400 font-mono text-sm" data-testid="protocol-detail-loading">
        {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="protocol-detail-view">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="font-mono text-xs text-slate-500">
            {protocol.Protocol_ID} · {t("common.version")} {protocol.Version_Courante}
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{protocol.Titre}</h1>
          <div className="flex items-center gap-2 mt-2">
            <StatusBadge status={protocol.Statut} />
            <span className="text-xs text-slate-500" data-testid="protocol-compliance-score">
              {t("common.compliance_score")}: {protocol.Score_Conformite}%
            </span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {protocol.Statut === "BROUILLON" && canStaff && !editMode && (
            <Button variant="outline" size="sm" data-testid="btn-edit-protocol" onClick={startEdit}>
              {t("common.edit")}
            </Button>
          )}
          {protocol.Statut === "BROUILLON" && canStaff && !editMode && (
            <Button size="sm" data-testid="btn-submit-protocol" onClick={() => action("soumettre", null, t("common.submit"))}>
              {t("common.submit")}
            </Button>
          )}
          {protocol.Statut === "A_VALIDER" && canValidate && (
            <>
              <Button size="sm" data-testid="btn-validate-protocol" onClick={() => action("valider", null, t("common.validate"))}>
                {t("common.validate")}
              </Button>
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="destructive" size="sm" data-testid="btn-refuse-protocol">
                    {t("common.refuse")}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t("common.refuse")}</DialogTitle>
                  </DialogHeader>
                  <Textarea data-testid="input-refuse-motif" placeholder={t("common.reason_required")} value={motif} onChange={(e) => setMotif(e.target.value)} />
                  <DialogFooter>
                    <Button data-testid="btn-confirm-refuse" onClick={() => action("refuser", { motif }, t("common.refuse"))}>
                      {t("common.confirm")}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
          {protocol.Statut === "VALIDE" && canValidate && (
            <Button size="sm" data-testid="btn-publish-protocol" onClick={() => action("publier", null, t("common.publish"))}>
              {t("common.publish")}
            </Button>
          )}
          {(protocol.Statut === "PUBLIE" || protocol.Statut === "BROUILLON") && canValidate && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="btn-archive-protocol">
                  {t("common.archive")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("common.archive")}</DialogTitle>
                </DialogHeader>
                <Textarea data-testid="input-archive-motif" placeholder={t("common.reason_optional")} value={motif} onChange={(e) => setMotif(e.target.value)} />
                <DialogFooter>
                  <Button data-testid="btn-confirm-archive" onClick={() => action("archiver", { motif }, t("common.archive"))}>
                    {t("common.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {protocol.Statut === "PUBLIE" && canStaff && (
            <Dialog>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="btn-new-version">
                  {t("common.new_version")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t("common.new_version")}</DialogTitle>
                </DialogHeader>
                <Select value={versionType} onValueChange={setVersionType}>
                  <SelectTrigger data-testid="select-version-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MINEURE">{t("common.minor")}</SelectItem>
                    <SelectItem value="MAJEURE">{t("common.major")}</SelectItem>
                  </SelectContent>
                </Select>
                <DialogFooter>
                  <Button data-testid="btn-confirm-new-version" onClick={() => action("nouvelle-version", { type_modification: versionType }, t("common.new_version"))}>
                    {t("common.confirm")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {canStaff && !editMode && (
            <Button variant="ghost" size="sm" data-testid="btn-duplicate-protocol" onClick={() => action("dupliquer", null, t("common.duplicate"))}>
              {t("common.duplicate")}
            </Button>
          )}
          {!editMode && (
            <Button variant="outline" size="sm" data-testid="btn-generate-pdf" onClick={downloadPdf}>
              <FileDown className="w-4 h-4" />
              PDF
            </Button>
          )}
          {!editMode && canStaff && (
            <div className="flex items-center gap-1" data-testid="translate-controls">
              <Select value={targetLang} onValueChange={setTargetLang}>
                <SelectTrigger className="w-32 h-9" data-testid="select-target-lang">
                  <SelectValue placeholder={t("common.target_language")} />
                </SelectTrigger>
                <SelectContent>
                  {langues.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`lang-option-${l.code}`}>
                      {l.nom}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" data-testid="btn-translate-protocol" onClick={() => translate()} disabled={translating || !targetLang}>
                <Languages className="w-4 h-4" />
                {translating ? t("common.translating") : t("common.translate")}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Tabs defaultValue="info">
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">
            {t("common.info")}
          </TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">
            {t("common.sections")}
          </TabsTrigger>
          <TabsTrigger value="cadre" data-testid="tab-cadre">
            {t("cadre.title")}
          </TabsTrigger>
          <TabsTrigger value="conformite" data-testid="tab-conformite">
            {t("common.compliance")}
          </TabsTrigger>
          <TabsTrigger value="traduction" data-testid="tab-traduction">
            {t("common.translation")}
          </TabsTrigger>
          <TabsTrigger value="versions" data-testid="tab-versions">
            {t("common.versions")}
          </TabsTrigger>
          <TabsTrigger value="historique" data-testid="tab-historique">
            {t("common.history")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="info" className="space-y-3 pt-4">
          {editMode ? (
            <div className="space-y-3 max-w-xl">
              <Input data-testid="edit-titre" value={editForm.titre} onChange={(e) => setEditForm({ ...editForm, titre: e.target.value })} />
              <Textarea data-testid="edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} />
              <Input data-testid="edit-echeance" type="date" value={editForm.date_echeance} onChange={(e) => setEditForm({ ...editForm, date_echeance: e.target.value })} />
              <div className="flex gap-2">
                <Button data-testid="btn-save-edit" onClick={saveEdit}>
                  {t("common.save")}
                </Button>
                <Button variant="outline" data-testid="btn-cancel-edit" onClick={() => setEditMode(false)}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 text-sm max-w-2xl">
              <div>
                <p className="text-slate-500">{t("common.site")}</p>
                <p className="font-medium text-slate-800 dark:text-slate-200">{protocol.Site_Nom}</p>
              </div>
              <div>
                <p className="text-slate-500">{t("common.operation")}</p>
                <p className="font-medium text-slate-800 dark:text-slate-200">{protocol.Type_Operation_Nom}</p>
              </div>
              <div>
                <p className="text-slate-500">{t("common.vehicule_type")}</p>
                {protocol.Type_Vehicule_Code ? (
                  <div className="flex items-center gap-2 mt-0.5" data-testid="protocol-vehicule-pictogram">
                    <VehiclePictogram vehicule={vehiculesList.find((v) => v.Code === protocol.Type_Vehicule_Code)} size={30} />
                    <span className="font-medium text-slate-800 dark:text-slate-200">
                      {vehiculesList.find((v) => v.Code === protocol.Type_Vehicule_Code)?.Nom || protocol.Type_Vehicule_Code}
                    </span>
                  </div>
                ) : (
                  <p className="font-medium text-slate-800 dark:text-slate-200">{t("common.none")}</p>
                )}
              </div>
              <div>
                <p className="text-slate-500">{t("common.produit")}</p>
                <p className="font-medium text-slate-800 dark:text-slate-200">{protocol.Produit_ID || t("common.none")}</p>
              </div>
              <div>
                <p className="text-slate-500">{t("common.author")}</p>
                <p className="font-medium text-slate-800 dark:text-slate-200">{protocol.Auteur_Nom}</p>
              </div>
              <div>
                <p className="text-slate-500">{t("common.due_date")}</p>
                <p className="font-medium text-slate-800 dark:text-slate-200">{protocol.Date_Echeance || "—"}</p>
              </div>
              <div className="sm:col-span-2">
                <p className="text-slate-500">{t("common.description")}</p>
                <p className="text-slate-700 dark:text-slate-300">{protocol.Description || "—"}</p>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sections" className="space-y-3 pt-4">
          {editMode
            ? editForm.sections.map((s, idx) => (
                <div key={idx} className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-2" data-testid={`edit-section-${idx}`}>
                  <Input value={s.titre_section} onChange={(e) => updateEditSection(idx, "titre_section", e.target.value)} />
                  <Textarea value={s.contenu} onChange={(e) => updateEditSection(idx, "contenu", e.target.value)} rows={3} />
                </div>
              ))
            : protocol.sections?.map((s, idx) => (
                <div key={s.Section_ID || idx} className="border border-slate-200 dark:border-slate-800 rounded-lg p-4" data-testid={`section-view-${idx}`}>
                  <p className="text-xs font-mono uppercase text-amber-600 dark:text-amber-400">{s.Type_Section}</p>
                  <p className="font-semibold text-slate-800 dark:text-slate-200 mb-2">{s.Titre_Section}</p>
                  {s.Type_Section === "EPI" || s.Type_Section === "RISQUES" ? (
                    <PictogramSectionContent type={s.Type_Section} contenu={s.Contenu} epiList={epiList} risquesList={risquesList} />
                  ) : (
                    <p className="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{s.Contenu}</p>
                  )}
                </div>
              ))}
        </TabsContent>

        <TabsContent value="cadre" className="space-y-4 pt-4 max-w-2xl" data-testid="cadre-tab-content">
          {editMode ? (
            <div className="grid sm:grid-cols-2 gap-3">
              {CADRE_FIELDS.map((f) =>
                f === "operation_repetitive" ? (
                  <div key={f} className="flex items-center justify-between border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2">
                    <Label>{t(`cadre.${f}`)}</Label>
                    <Switch data-testid={`edit-${f}`} checked={!!editForm[f]} onCheckedChange={(v) => setEditForm({ ...editForm, [f]: v })} />
                  </div>
                ) : (
                  <div key={f} className="space-y-1.5">
                    <Label>{t(`cadre.${f}`)}</Label>
                    <Input data-testid={`edit-${f}`} value={editForm[f] || ""} onChange={(e) => setEditForm({ ...editForm, [f]: e.target.value })} />
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {CADRE_FIELDS.map((f) => (
                <div key={f}>
                  <p className="text-slate-500">{t(`cadre.${f}`)}</p>
                  <p className="font-medium text-slate-800 dark:text-slate-200" data-testid={`cadre-view-${f}`}>
                    {f === "operation_repetitive" ? (protocol[CADRE_SHEET_KEYS[f]] ? t("cadre.oui") : t("cadre.non")) : protocol[CADRE_SHEET_KEYS[f]] || "—"}
                  </p>
                </div>
              ))}
            </div>
          )}
          <div className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-2">
            <p className="font-semibold text-slate-800 dark:text-slate-200">{t("cadre.plan_circulation")}</p>
            {protocol.Plan_Circulation_URL ? (
              <a href={protocol.Plan_Circulation_URL} target="_blank" rel="noreferrer" className="text-xs text-amber-600 dark:text-amber-400 underline" data-testid="link-plan-circulation">
                {t("cadre.plan_circulation_view")}
              </a>
            ) : (
              <p className="text-xs text-slate-500" data-testid="plan-circulation-empty">{t("cadre.plan_circulation_empty")}</p>
            )}
            {canStaff && (
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer text-amber-600 dark:text-amber-400">
                <Upload className="w-4 h-4" />
                {uploadingPlan ? t("common.loading") : t("cadre.plan_circulation_upload")}
                <input type="file" accept="application/pdf,image/png,image/jpeg" className="hidden" data-testid="input-plan-circulation" onChange={uploadPlanCirculation} disabled={uploadingPlan} />
              </label>
            )}
          </div>
        </TabsContent>

        <TabsContent value="versions" className="space-y-2 pt-4">
          {versions.map((v) => (
            <div
              key={v.Version_ID}
              className="flex items-center justify-between border border-slate-200 dark:border-slate-800 rounded-lg p-3 text-sm"
              data-testid={`version-row-${v.Numero_Version}`}
            >
              <div>
                <p className="font-mono font-semibold text-slate-800 dark:text-slate-200">
                  v{v.Numero_Version} · {v.Type_Modification}
                </p>
                <p className="text-xs text-slate-500">{v.Commentaire}</p>
              </div>
              <StatusBadge status={v.Statut_Version} />
            </div>
          ))}
        </TabsContent>

        <TabsContent value="historique" className="space-y-2 pt-4">
          {historique.map((h) => (
            <div key={h.Historique_ID} className="border-l-2 border-slate-300 dark:border-slate-700 pl-3 py-1 text-sm" data-testid={`history-entry-${h.Historique_ID}`}>
              <p className="text-slate-700 dark:text-slate-300">
                <span className="font-semibold">{h.Action}</span> — {h.Utilisateur_Email}
              </p>
              <p className="text-xs text-slate-500">
                {new Date(h.Date_Heure).toLocaleString()} {h.Motif && `· ${h.Motif}`}
              </p>
            </div>
          ))}
        </TabsContent>
        <TabsContent value="conformite" className="space-y-2 pt-4">
          {referentiel && referentiel.regles.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="conformite-empty">
              {t("common.no_applicable_rule")}
            </p>
          )}
          {referentiel?.regles.map((r) => (
            <div
              key={r.Regle_ID}
              className={`flex items-start justify-between gap-3 border rounded-lg p-3 text-sm ${
                r.Couverte ? "border-emerald-300 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20" : "border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/20"
              }`}
              data-testid={`regle-row-${r.Regle_ID}`}
            >
              <div>
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {r.Regle_ID} · {r.Libelle}
                </p>
                <p className="text-xs text-slate-500">
                  {t("common.level")} {r.Niveau_Referentiel} · {r.Source_Reglementaire}
                </p>
              </div>
              <span className={`text-xs font-semibold whitespace-nowrap ${r.Couverte ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {r.Couverte ? t("common.covered") : t("common.not_covered")}
              </span>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="traduction" className="space-y-3 pt-4 max-w-2xl">
          {traductions.length === 0 && (
            <p className="text-sm text-slate-500" data-testid="traduction-empty">
              {t("common.no_translation_yet")}
            </p>
          )}
          {traductions.map((tr) => (
            <div key={tr.Traduction_ID} className="border border-slate-200 dark:border-slate-800 rounded-lg p-4 space-y-2" data-testid={`traduction-block-${tr.Traduction_ID}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold text-slate-800 dark:text-slate-200">
                  {tr.Langue} · {tr.Statut === "EN_COURS" ? t("common.translating") : tr.Statut === "ECHEC" ? t("common.translation_failed") : tr.Statut}
                </p>
                {tr.PDF_URL && (
                  <a href={tr.PDF_URL} target="_blank" rel="noreferrer" className="text-xs text-amber-600 dark:text-amber-400 underline" data-testid="link-pdf-en">
                    {t("common.download_pdf_en")}
                  </a>
                )}
              </div>
              {tr.Obsolete && tr.Statut !== "EN_COURS" && (
                <div className="flex items-center justify-between gap-2 bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-md p-2" data-testid={`traduction-obsolete-${tr.Traduction_ID}`}>
                  <span className="text-xs font-medium text-red-700 dark:text-red-400">{t("common.translation_obsolete_warning")}</span>
                  <Button size="sm" variant="outline" data-testid="btn-retranslate" onClick={() => translate(tr.Langue)} disabled={translating}>
                    {t("common.retranslate")}
                  </Button>
                </div>
              )}
              <p className="font-medium text-slate-700 dark:text-slate-300">{tr.Contenu?.titre}</p>
              {tr.Contenu?.sections?.map((s, idx) => (
                <div key={idx} className="text-sm">
                  <p className="font-semibold text-slate-700 dark:text-slate-300">{s.titre_section}</p>
                  <p className="text-slate-600 dark:text-slate-400 whitespace-pre-wrap">{s.contenu}</p>
                </div>
              ))}
            </div>
          ))}
        </TabsContent>

      </Tabs>
    </div>
  );
}
